// src/components/Common/RichTextEditor.js
import React, { useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createQuillImageHandler } from '../../utils/imageUpload';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Start writing your blog post...",
  height = "400px",
  readOnly = false,
  theme = "snow"
}) => {
  const [uploading, setUploading] = useState(false);

  // Custom toolbar configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: createQuillImageHandler(
          () => setUploading(true),
          () => setUploading(false)
        )
      }
    },
    clipboard: {
      matchVisual: false,
    },
    history: {
      delay: 1000,
      maxStack: 50,
      userOnly: true
    }
  }), []);

  // Custom formats
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  return (
    <div className={`rich-text-editor ${uploading ? 'loading' : ''}`}>
      {uploading && (
        <div className="upload-indicator">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Uploading image...</span>
          </div>
          <span className="ms-2">Uploading image...</span>
        </div>
      )}
      <ReactQuill
        theme={theme}
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly || uploading}
        style={{
          height: height,
          marginBottom: '50px' // Space for toolbar
        }}
      />
      
      {/* Custom CSS for better styling */}
      <style jsx="true" global="true">{`
        .rich-text-editor .ql-editor {
          min-height: ${height};
          font-size: 16px;
          line-height: 1.6;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }
        
        .rich-text-editor .ql-toolbar {
          border-top: 1px solid #ccc;
          border-left: 1px solid #ccc;
          border-right: 1px solid #ccc;
          border-bottom: none;
          border-radius: 8px 8px 0 0;
          background-color: #f8f9fa;
        }
        
        .rich-text-editor .ql-container {
          border-bottom: 1px solid #ccc;
          border-left: 1px solid #ccc;
          border-right: 1px solid #ccc;
          border-top: none;
          border-radius: 0 0 8px 8px;
          font-size: 16px;
        }
        
        .rich-text-editor .ql-editor:focus {
          outline: none;
        }
        
        .rich-text-editor .ql-toolbar .ql-picker-label:hover,
        .rich-text-editor .ql-toolbar .ql-picker-item:hover {
          color: #7b00ff;
        }
        
        .rich-text-editor .ql-toolbar button:hover,
        .rich-text-editor .ql-toolbar button:focus {
          color: #7b00ff;
        }
        
        .rich-text-editor .ql-toolbar button.ql-active {
          color: #7b00ff;
        }
        
        .rich-text-editor .ql-toolbar .ql-picker.ql-expanded .ql-picker-label {
          color: #7b00ff;
          border-color: #7b00ff;
        }
        
        .rich-text-editor .ql-editor blockquote {
          border-left: 4px solid #7b00ff;
          padding-left: 16px;
          margin-left: 0;
          margin-right: 0;
          background-color: #f8f9fa;
          padding: 12px 16px;
          border-radius: 4px;
        }
        
        .rich-text-editor .ql-editor pre.ql-syntax {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 12px;
          overflow-x: auto;
        }
        
        .rich-text-editor .ql-editor h1,
        .rich-text-editor .ql-editor h2,
        .rich-text-editor .ql-editor h3,
        .rich-text-editor .ql-editor h4,
        .rich-text-editor .ql-editor h5,
        .rich-text-editor .ql-editor h6 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        
        .rich-text-editor .ql-editor h1 {
          font-size: 2.5em;
          color: #2c3e50;
        }
        
        .rich-text-editor .ql-editor h2 {
          font-size: 2em;
          color: #34495e;
        }
        
        .rich-text-editor .ql-editor h3 {
          font-size: 1.5em;
          color: #34495e;
        }
        
        .rich-text-editor .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 10px 0;
        }
        
        .rich-text-editor .ql-editor a {
          color: #7b00ff;
          text-decoration: none;
        }
        
        .rich-text-editor .ql-editor a:hover {
          text-decoration: underline;
        }
        
        .rich-text-editor .ql-editor ul,
        .rich-text-editor .ql-editor ol {
          padding-left: 1.5em;
        }
        
        .rich-text-editor .ql-editor li {
          margin-bottom: 0.5em;
        }
        
        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .rich-text-editor .ql-toolbar {
            padding: 8px;
          }
          
          .rich-text-editor .ql-formats {
            margin-right: 8px;
          }
          
          .rich-text-editor .ql-editor {
            font-size: 14px;
            padding: 12px;
          }
        }
        
        /* Upload indicator */
        .upload-indicator {
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(255, 255, 255, 0.95);
          padding: 8px 12px;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          display: flex;
          align-items: center;
          font-size: 14px;
          color: #7b00ff;
        }
        
        .upload-indicator .spinner-border {
          width: 1rem;
          height: 1rem;
          border-width: 2px;
          color: #7b00ff;
        }
        
        /* Loading state */
        .rich-text-editor.loading {
          position: relative;
        }
        
        .rich-text-editor.loading .ql-editor {
          opacity: 0.7;
        }
        
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;